import { ComponentProps, useEffect, useRef, useState } from 'react'
import {
  type Control,
  Controller,
  type FieldArrayWithId,
  type FieldPath,
  type FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form'

import * as Apollo from '@apollo/client'
import { Box, Text, TextField } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import {
  Delete,
  DollarReset,
  FileFilled,
  Pencil,
  Present,
  Target,
  Timer,
  Wallet,
} from '@factorialco/f0-react/icons/app'
import {
  CompensationsFormFieldsDocument,
  CompensationsListColumnsDocument,
  DeleteCompensationsEmployeesCompensationDocument,
  DeleteCompensationsEmployeesCompensationMutation,
  DeleteCompensationsEmployeesCompensationMutationVariables,
  EditCompensationsFormQuery,
  PayrollValueObjectsConceptUnitTypeEnum,
  UpsertCompensationsEmployeesCompensationDocument,
  UpsertCompensationsEmployeesCompensationMutation,
  UpsertCompensationsEmployeesCompensationMutationVariables,
  ValidationTypeFiltersDocument,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import DurationInput from 'modules/core/components/DurationInput'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { fromCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { useCompensationsRefresh } from 'modules/compensations/lib/CompensationsRefreshContext'
import { CompensationsAlertPopover } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/CompensationsAlertPopover'
import { DurationInputWithValidation } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/DurationInputWithValidation'
import {
  Formatter,
  MoneyInputField as MoneyField,
} from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/MoneyInputField'
import { NumberInputField } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/NumberInputField'
import { PercentagePopoverField } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/PercentagePopoverField'
import { formatValue } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/formatUtils'
import { isGraphQLType } from 'modules/contracts/lib/types'
import { usePayrollConceptCompensationUnits } from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import { mapAmountFromCents, mapAmountToCents } from './CompensationAmountInputFieldUtils'
import {
  deleteCompensationsEmployeesCompensationMutationCacheUpdater,
  upsertCompensationsEmployeesCompensationMutationCacheUpdater,
} from './cacheUpdaters'

type F0IconType = ComponentProps<typeof F0Icon>['icon']

type CompensationsVariableAmountStrategy = Extract<
  NonNullable<
    EditCompensationsFormQuery['compensations']['employeesCompensationsConnection']['nodes'][number]['compensationsAmountStrategy']
  >,
  { __typename: 'CompensationsVariableAmountStrategy' }
>

function useUpsertCompensationsEmployeesCompensationMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpsertCompensationsEmployeesCompensationMutation,
    UpsertCompensationsEmployeesCompensationMutationVariables
  >
) {
  const options = { ...baseOptions }
  return Apollo.useMutation<
    UpsertCompensationsEmployeesCompensationMutation,
    UpsertCompensationsEmployeesCompensationMutationVariables
  >(UpsertCompensationsEmployeesCompensationDocument, options)
}

function useDeleteCompensationsEmployeesCompensationMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeleteCompensationsEmployeesCompensationMutation,
    DeleteCompensationsEmployeesCompensationMutationVariables
  >
) {
  const options = { ...baseOptions }
  return Apollo.useMutation<
    DeleteCompensationsEmployeesCompensationMutation,
    DeleteCompensationsEmployeesCompensationMutationVariables
  >(DeleteCompensationsEmployeesCompensationDocument, options)
}

function variableAmountInputHintText({
  amountStrategy,
  unit,
  unitSymbol,
  locale,
}: {
  amountStrategy: CompensationsVariableAmountStrategy
  unit: PayrollValueObjectsConceptUnitTypeEnum
  unitSymbol: string
  locale: string
}) {
  const { upperLimit } = amountStrategy || {}

  const upperLimitValue = unit === 'money' ? fromCents(Number(upperLimit)) : Number(upperLimit)
  const hasUpperLimit = upperLimit !== undefined && upperLimit !== null && !isNaN(upperLimitValue)

  if (hasUpperLimit) {
    return `${i18n.t('compensations.common.upTo')} ${formatValue(
      upperLimitValue,
      unit,
      unitSymbol,
      locale
    )}`
  }

  return i18n.t('compensations.common.noLimit')
}

const numberFormatter: Formatter<number> = {
  format: (value) => String(value),
  parse: (value) => Number(value),
}

const COMPLETED_TIMEOUT = 5000
const DEBOUNCE_WAIT_TIME = 1000

function CompensationAmountInputField<TFieldValues extends FieldValues>({
  employeeId,
  payrollRunId,
  control,
  amountPath,
  remove,
  index,
  isDirty,
  onLoadSuccess,
  readonly,
}: {
  employeeId: string
  payrollRunId: string
  control: Control<TFieldValues>
  amountPath: FieldPath<TFieldValues>
  field?: FieldArrayWithId<FieldValues>
  remove: (index: number) => void
  index: number
  isDirty: boolean
  onLoadSuccess?: (isUpserting: boolean) => void
  readonly?: boolean
}) {
  const {
    access: { locale },
  } = useCurrentSession()

  const { refreshCompensations } = useCompensationsRefresh()
  const { setValue, reset, getValues } = useFormContext()

  const [upsertCompensationsEmployeesCompensation, { loading }] =
    useUpsertCompensationsEmployeesCompensationMutation()

  const [deleteCompensationsEmployeesCompensation] =
    useDeleteCompensationsEmployeesCompensationMutation()

  const [isDeleting, setIsDeleting] = useState(false)

  const compensation = useWatch({
    control,
    name: amountPath,
  })
  const payrollConceptUnit: PayrollValueObjectsConceptUnitTypeEnum =
    compensation?.payrollConcept.unitType

  const isCompensationFixedAmount = isGraphQLType(
    compensation.compensationsAmountStrategy,
    'CompensationsFixedAmountStrategy'
  )

  useEffect(() => {
    onLoadSuccess?.(loading)
  }, [loading, onLoadSuccess])

  const isOverridden: boolean = compensation.isOverridden
  const [completed, setCompleted] = useState<boolean>(false)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedMutationRef = useRef<ReturnType<typeof debounce> | null>(null)

  const unitSymbol = usePayrollConceptCompensationUnits(payrollConceptUnit)

  useEffect(() => {
    debouncedMutationRef.current = debounce((compensationData) => {
      const parsedCompensation = mapAmountToCents(compensationData)
      const amount: number | null = !parsedCompensation.isOverridden
        ? parsedCompensation.compensationsAmountStrategy?.amount
        : parsedCompensation.amount

      if (!parsedCompensation || amount === undefined || isDeleting) {
        return
      }

      setCompleted(true)

      if (compensationData.id) {
        track(trackingEvents.compensations.updateCompensation, {
          id: compensationData.id,
        })
      } else {
        track(trackingEvents.compensations.createCompensation, {
          type: 'manual',
          source: parsedCompensation.source,
          amount,
        })
      }

      return upsertCompensationsEmployeesCompensation({
        variables: {
          id: compensationData?.id,
          amount,
          employeeId,
          payrollRunId,
          payrollConceptId: compensationData.payrollConcept?.id,
          unitSymbol,
          isOverwritten: compensationData.isOverridden,
        },
        onCompleted: (data) => {
          timeoutRef.current = setTimeout(() => setCompleted(false), COMPLETED_TIMEOUT)

          const editedCompensation =
            data.compensationsMutations?.upsertCompensationsEmployeesCompensation
              ?.employeesCompensation

          // TODO: we need to check if reverting this to just update the id doesn't break hint text use cases
          // The rationale behind only updating id is because useArrayFields uses its internal id intended
          // for manage react keys, so if we override it with setValue the already existing ids, the react
          // array is broken, and therefore the experience.
          // Definitely we should look for an alternative path since this is a big indirection hard
          // to maintain and error prone.
          // https://github.com/orgs/react-hook-form/discussions/8935
          if (editedCompensation?.id !== null && editedCompensation?.id !== undefined) {
            setValue(`${String(amountPath)}`, mapAmountFromCents(editedCompensation), {
              shouldDirty: false,
              shouldTouch: false,
            })
          }
          reset(getValues())

          refreshCompensations()
        },
        update: (cache) => {
          upsertCompensationsEmployeesCompensationMutationCacheUpdater(cache)
        },
        // TODO: Check why we need optimistic response on this use case
        optimisticResponse: {
          compensationsMutations: {
            __typename: 'compensationsMutations',
            upsertCompensationsEmployeesCompensation: {
              __typename: 'UpsertCompensationsEmployeesCompensationPayload',
              employeesCompensation: {
                __typename: 'CompensationsEmployeesCompensation',
                id: compensationData?.id || 'temp-id',
                amount: !compensationData.isOverridden
                  ? compensationData.compensationsAmountStrategy?.amount
                  : compensationData.amount,
                amountStrategyId: compensationData?.compensationsAmountStrategy?.id || 'tmp-id',
                amountStrategyType: compensationData?.compensationsAmountStrategy?.__typename,
                compensationsAmountStrategy: compensationData?.compensationsAmountStrategy,
                isOverridden: compensationData.isOverridden,
                payrollConcept: compensationData?.payrollConcept,
                createdAt: compensationData?.createdAt,
                updatedAt: compensationData?.updatedAt,
                createdByEmployee: compensationData?.createdByEmployee,
                lastUpdatedByEmployee: compensationData?.lastUpdatedByEmployee,
                validations: compensationData?.validations,
              },
            },
          },
        },
        refetchQueries: [
          CompensationsListColumnsDocument,
          CompensationsFormFieldsDocument,
          ValidationTypeFiltersDocument,
        ],
      })
    }, DEBOUNCE_WAIT_TIME)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (debouncedMutationRef.current) {
        debouncedMutationRef.current.cancel()
      }
    }
  }, [
    employeeId,
    isDeleting,
    upsertCompensationsEmployeesCompensation,
    setValue,
    amountPath,
    compensation?.id,
    unitSymbol,
    reset,
    getValues,
    payrollRunId,
    refreshCompensations,
  ])

  useEffect(() => {
    if (compensation && isDirty && !isDeleting && debouncedMutationRef.current) {
      debouncedMutationRef.current(compensation)
    }
  }, [compensation, isDirty, isDeleting])

  const isFromContract = compensation.sourceType === 'compensations_compensationpolicy'
  const isFromTime = compensation.sourceType === 'timesettings_customtimerangecategory'
  const isFromExpense =
    compensation.sourceType === 'expenses_expensable' ||
    compensation.sourceType === 'expenses_expense' ||
    compensation.sourceType === 'expenses_mileage'
  const isFromBenefits = compensation.sourceType === 'benefits_compensation'
  const isFromSalaryAdvance = compensation.sourceType === 'salary_advance_request'
  const isFromIncentives = compensation.sourceType === 'incentives_compensationplanrequest'

  const isAmountReadonly = readonly || isFromIncentives

  const formatCompensationDate = () => {
    return new Date(
      compensation.updatedAt || compensation.createdAt || Date.now()
    ).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const getAuthorEmployeeName = () => {
    const lastUpdatedByEmployee = compensation.lastUpdatedByEmployee
    const createdByEmployee = compensation.createdByEmployee

    if (lastUpdatedByEmployee?.firstName && lastUpdatedByEmployee?.lastName) {
      return `${lastUpdatedByEmployee.firstName} ${lastUpdatedByEmployee.lastName}`
    }

    if (createdByEmployee?.firstName && createdByEmployee?.lastName) {
      return `${createdByEmployee.firstName} ${createdByEmployee.lastName}`
    }

    return ''
  }

  const hintText = () => {
    let baseText = ''

    if (isOverridden) {
      baseText = i18n.t('compensations.CompensationFormFields.manualInput')
    } else if (isFromContract) {
      if (isCompensationFixedAmount) {
        baseText = i18n.t('compensations.common.contract_fixed')
      } else {
        baseText = `${i18n.t('compensations.common.contract')}: ${variableAmountInputHintText({
          amountStrategy: compensation.compensationsAmountStrategy,
          unit: payrollConceptUnit,
          unitSymbol,
          locale,
        })}`
      }
    } else if (isFromTime) {
      baseText = i18n.t('compensations.common.time')
    } else if (isFromExpense) {
      baseText = i18n.t('compensations.common.spending')
    } else if (isFromBenefits) {
      baseText = i18n.t('compensations.common.benefits')
    } else if (isFromSalaryAdvance) {
      baseText = i18n.t('compensations.common.salary_advance_request')
    } else if (isFromIncentives) {
      baseText = `${i18n.t(
        'compensations.common.incentives_compensation_plan'
      )}: ${variableAmountInputHintText({
        amountStrategy: compensation.compensationsAmountStrategy,
        unit: payrollConceptUnit,
        unitSymbol,
        locale,
      })}`
    }

    const author = getAuthorEmployeeName()
    if (author) {
      return `${baseText} • ${formatCompensationDate()} • ${author}`
    } else {
      return baseText
    }
  }

  const sourceTypeToIcon: Record<string, F0IconType> = {
    compensations_compensationpolicy: FileFilled,
    timesettings_customtimerangecategory: Timer,
    expenses_expensable: Wallet,
    expenses_expense: Wallet,
    expenses_mileage: Wallet,
    benefits_compensation: Present,
    salary_advance_request: DollarReset,
    incentives_compensationplanrequest: Target,
  }

  const icon: F0IconType | undefined = isOverridden
    ? Pencil
    : sourceTypeToIcon[compensation.sourceType]

  const isButtonDisabled =
    isCompensationFixedAmount ||
    isAmountReadonly ||
    isFromContract

  const isVariableWithLimit =
    compensation.compensationsAmountStrategy.upperLimit !== null &&
    compensation.compensationsAmountStrategy.upperLimit !== undefined

  const Unit = () => (
    <Box alignItems="center" justifyContent="center" width="full">
      {unitSymbol}
    </Box>
  )

  return (
    <Box gap="s8">
      <Box flexDirection="row" gap="s16" alignItems="center">
        {isOverridden ? (
          payrollConceptUnit === 'time' ? (
            <Box width="full">
              <Controller
                control={control}
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                name={`${amountPath}.amount` as FieldPath<TFieldValues>}
                render={({ field }) => {
                  // TODO: Currently we don't have a completed state on the duration input, so we're using the DurationInputWithValidation component as a workaround.
                  // We should remove the DurationInputWithValidation component once we have a completed state on the DurationInput component.
                  return (
                    <DurationInputWithValidation
                      completed={completed}
                      {...field}
                      minutes={compensation?.amount}
                    />
                  )
                }}
              />
            </Box>
          ) : payrollConceptUnit === 'money' ? (
            <MoneyField
              readonly={isAmountReadonly}
              control={control}
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              name={`${amountPath}.amount` as FieldPath<TFieldValues>}
              placeholder={i18n.t('compensations.common.placeholder')}
              completed={completed}
              formatter={numberFormatter}
            />
          ) : (
            <NumberInputField
              readonly={isAmountReadonly}
              control={control} // eslint-disable-next-line no-type-assertion/no-type-assertion
              name={`${amountPath}.amount` as FieldPath<TFieldValues>}
              placeholder={i18n.t('compensations.common.placeholder')}
              completed={completed}
              indicator={<Unit />}
            />
          )
        ) : isCompensationFixedAmount ? (
          payrollConceptUnit === 'time' ? (
            <Box width="full">
              <Controller
                control={control}
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                name={`${amountPath}.compensationsAmountStrategy.amount` as FieldPath<TFieldValues>}
                render={({ field }) => {
                  // TODO: there is not completed state on this input
                  return (
                    <DurationInput
                      {...field}
                      readonly
                      label={'Amount'}
                      minutes={compensation.compensationsAmountStrategy.amount}
                    />
                  )
                }}
              />
            </Box>
          ) : payrollConceptUnit === 'money' ? (
            <MoneyField
              readonly
              control={control}
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              name={`${amountPath}.compensationsAmountStrategy.amount` as FieldPath<TFieldValues>}
              placeholder={i18n.t('compensations.common.placeholder')}
              completed={completed}
              formatter={numberFormatter}
            />
          ) : (
            <Controller
              control={control}
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              name={`${amountPath}.compensationsAmountStrategy.amount` as FieldPath<TFieldValues>}
              render={({ field }) => {
                return (
                  <TextField
                    {...field}
                    readonly
                    completed={completed}
                    placeholder="Amount..."
                    value={(compensation.compensationsAmountStrategy.amount ?? 0).toString()}
                    missingLabel={i18n.t('common.missing_field')}
                    selectValue={i18n.t('common.select')}
                    indicator={<Unit />}
                  />
                )
              }}
            />
          )
        ) : payrollConceptUnit === 'time' ? (
          <Box width="full">
            <Controller
              control={control}
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              name={`${amountPath}.compensationsAmountStrategy.amount` as FieldPath<TFieldValues>}
              render={({ field }) => {
                // TODO: there is not completed state on this input
                return (
                  <DurationInput
                    {...field}
                    minutes={compensation?.compensationsAmountStrategy.amount}
                    readonly={isAmountReadonly}
                  />
                )
              }}
            />
          </Box>
        ) : (
          <>
            {payrollConceptUnit === 'money' ? (
              <>
                {isVariableWithLimit && (
                  <PercentagePopoverField
                    name={`${amountPath}.compensationsAmountStrategy`}
                    readonly={isAmountReadonly}
                  />
                )}
                <MoneyField
                  control={control}
                  placeholder={i18n.t('compensations.common.placeholder')}
                  name={
                    // eslint-disable-next-line no-type-assertion/no-type-assertion
                    `${amountPath}.compensationsAmountStrategy.amount` as FieldPath<TFieldValues>
                  }
                  completed={completed}
                  readonly={isAmountReadonly}
                  formatter={numberFormatter}
                />
              </>
            ) : (
              <NumberInputField
                control={control}
                placeholder={i18n.t('compensations.common.placeholder')}
                // @ts-expect-error when register input name, you will have to cast them as const
                name={`${amountPath}.compensationsAmountStrategy.amount` as const}
                completed={completed}
                indicator={<Unit />}
                readonly={isAmountReadonly}
              />
            )}
          </>
        )}
        <Box alignSelf="flexEnd" paddingBottom="s6" flexDirection="row" gap="s8">
          <CompensationsAlertPopover
            readonly={readonly}
            shouldShowWarning={compensation.validations && compensation.validations.length > 0}
            validations={compensation.validations}
          />
          <F0Button
            variant="outline"
            icon={Delete}
            hideLabel
            label={
              isButtonDisabled
                ? i18n.t('compensations.payroll_run.edit_compensations.delete.tooltip.label')
                : i18n.t('compensations.payroll_run.edit_compensations.delete.button')
            }
            type="button"
            disabled={isButtonDisabled}
            onClick={() => {
              if (compensation.id) {
                setIsDeleting(true)
                track(trackingEvents.compensations.deleteCompensation, {
                  id: compensation.id,
                })

                const compensationId = compensation.id

                return deleteCompensationsEmployeesCompensation({
                  variables: {
                    id: compensationId,
                  },
                  onCompleted: () => {
                    remove(index)
                    setIsDeleting(false)
                    refreshCompensations()
                  },
                  onError: () => {
                    setIsDeleting(false)
                  },
                  update: (cache) => {
                    deleteCompensationsEmployeesCompensationMutationCacheUpdater(
                      cache,
                      compensationId
                    )
                  },
                  refetchQueries: [
                    CompensationsListColumnsDocument,
                    CompensationsFormFieldsDocument,
                    ValidationTypeFiltersDocument,
                  ],
                })
              } else {
                remove(index)
              }
            }}
          />
        </Box>
      </Box>
      <Box flexDirection="row" gap="s16" alignItems="center">
        <Box
          paddingLeft={
            !isCompensationFixedAmount && payrollConceptUnit === 'money' && isVariableWithLimit
              ? 's48'
              : 's4'
          }
          flexDirection="row"
          alignItems="center"
        >
          {icon && <F0Icon icon={icon} size="md" color="secondary" />}
          <Text.Normal size="100">{hintText()}</Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}

export { CompensationAmountInputField }
